import { createTheme, type Theme } from '@mui/material/styles';
import { brand, darkPalette, lightPalette, type Density, type ThemeMode } from './tokens';

/**
 * Build the MUI theme for a mode + density. Density drives base spacing and the
 * default size of dense components so "compact" genuinely tightens the UI (spec §4).
 */
export function createAppTheme(mode: ThemeMode, density: Density): Theme {
  const isDark = mode === 'dark';
  const p = isDark ? darkPalette : lightPalette;
  const compact = density === 'compact';

  return createTheme({
    palette: {
      mode,
      primary: { main: brand.primary, dark: brand.primaryDark },
      secondary: { main: brand.secondary },
      success: { main: brand.success },
      warning: { main: brand.warning },
      error: { main: brand.error },
      info: { main: brand.info },
      background: { default: p.background, paper: p.paper },
      text: { primary: p.textPrimary, secondary: p.textSecondary },
      divider: p.divider,
    },
    shape: { borderRadius: 10 },
    spacing: compact ? 6 : 8,
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", system-ui, -apple-system, sans-serif',
      fontSize: compact ? 13 : 14,
      h1: { fontSize: '1.9rem', fontWeight: 700 },
      h2: { fontSize: '1.55rem', fontWeight: 700 },
      h3: { fontSize: '1.3rem', fontWeight: 600 },
      h4: { fontSize: '1.15rem', fontWeight: 600 },
      h5: { fontSize: '1rem', fontWeight: 600 },
      h6: { fontSize: '0.9rem', fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true, size: compact ? 'small' : 'medium' },
      },
      MuiTextField: {
        defaultProps: { size: compact ? 'small' : 'medium', fullWidth: true },
      },
      MuiTable: { defaultProps: { size: compact ? 'small' : 'medium' } },
      MuiCard: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: { root: { borderRadius: 12 } },
      },
      MuiAppBar: {
        defaultProps: { color: 'default', elevation: 0 },
        styleOverrides: {
          root: { backgroundColor: p.paper, borderBottom: `1px solid ${p.divider}` },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
      },
    },
  });
}
